import React, { useState } from "react";

import { AddCircle, Refresh } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { ChangeSetClient, ChangeSetViewModel, ChangeSetViewModelPagingResultModel } from "../../../api/api";
import useApiClient from "../../../common/hooks/useApiClient";
import { CreateChangeSetDialog } from "../../ChangeSets";

interface ChangeSetSelectionDialogProps {
	open: boolean;
	onClose: () => void;
	onSelect: (changeSet: ChangeSetViewModel) => void;
	onRemove?: () => void;
	currentChangeSetId?: string;
	title?: string;
	excludeIds?: string[];
}

export default function ChangeSetSelectionDialog({
	open,
	onClose,
	onSelect,
	onRemove,
	currentChangeSetId,
	title = "Select ChangeSet",
	excludeIds = [],
}: ChangeSetSelectionDialogProps) {
	const client = useApiClient(ChangeSetClient);
	const [openCreateDialog, setOpenCreateDialog] = useState(false);

	const { data, isLoading, refetch } = useQuery<ChangeSetViewModelPagingResultModel>({
		queryKey: ["changeSets", "active"],
		queryFn: async () => client.search(undefined, true, 0, 100, "name", true),
		enabled: open,
	});

	const handleSelect = (changeSet: ChangeSetViewModel) => {
		onSelect(changeSet);
		// Note: Dialog closing is handled by the parent component
	};

	const handleCreateSuccess = () => {
		refetch();
		setOpenCreateDialog(false);
	};

	// Filter out already associated ChangeSets
	const availableChangeSets = data?.items?.filter((cs) => !excludeIds.includes(cs.id)) || [];

	const getStatusColor = (status: string) => {
		switch (status) {
			case "Active":
				return "success";
			case "Complete":
				return "info";
			case "Cancelled":
				return "default";
			default:
				return "default";
		}
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">{title}</Typography>
						<Stack direction="row" spacing={1}>
							<IconButton size="small" onClick={() => refetch()} title="Refresh ChangeSets">
								<Refresh />
							</IconButton>
							<Button variant="outlined" size="small" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)} title="Create new ChangeSet">
								Create New
							</Button>
						</Stack>
					</Stack>
				</DialogTitle>

				<DialogContent>
					<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
						Select an active ChangeSet to associate with this environment
					</Typography>

					{isLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress />
						</Box>
					) : availableChangeSets.length === 0 ? (
						<Paper sx={{ p: 3, textAlign: "center", bgcolor: "grey.50" }}>
							<Typography variant="body2" color="textSecondary">
								{excludeIds.length > 0 && data?.items?.length === excludeIds.length
									? "All available ChangeSets are already excluded."
									: "No active ChangeSets found."}
							</Typography>
							<Button variant="outlined" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)} sx={{ mt: 2 }}>
								Create New ChangeSet
							</Button>
						</Paper>
					) : (
						<List sx={{ maxHeight: 400, overflow: "auto" }}>
							{availableChangeSets.map((changeSet) => (
								<ListItem key={changeSet.id} disablePadding>
									<ListItemButton onClick={() => handleSelect(changeSet)}>
										<ListItemText
											primary={
												<Stack direction="row" spacing={2} alignItems="center">
													<Typography variant="body2">{changeSet.name}</Typography>
													<Chip label={changeSet.status} size="small" color={getStatusColor(changeSet.status)} />
												</Stack>
											}
											secondary={changeSet.description || "No description"}
										/>
									</ListItemButton>
								</ListItem>
							))}
						</List>
					)}
				</DialogContent>

				<DialogActions>
					{currentChangeSetId && onRemove && (
						<Button onClick={onRemove} color="error" sx={{ mr: "auto" }}>
							Remove ChangeSet
						</Button>
					)}
					<Button onClick={onClose}>Cancel</Button>
				</DialogActions>
			</Dialog>

			<CreateChangeSetDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={handleCreateSuccess} />
		</>
	);
}
